import * as _ from 'underscore';
import { appStore } from '../../app/AppStore';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { CommonErrorCode, GroupErrorCode, LdapErrorCode, UserErrorCode } from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { IUserInfo } from '../models/IUserInfo';
import { IErrorItem, MultipleItemResult } from '../models/MultipleItemResult';
import { IIdItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IUpdateUsersGroupMembershipResult } from '../ServerApi/requests/UpdateUsersGroupMembershipRequest';
import { IError } from '../ServerApi/types/IError';
import { IResult } from '../ServerApi/types/IResponse';
import {
  ContentActionNotification,
  IMultipleResultNotificationOptions,
  MultipleResultNotificationOptions,
} from './ContentActionNotification';


// Notification For:
// => group membership for users

export class GroupMembershipUpdateActionNotification {

  public static $inject = [
    '$translate',
    'ContentActionNotification'];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private contentActionNotification: ContentActionNotification) {}

  public notify(
    result: IUpdateUsersGroupMembershipResult,
    users: Set<IUserInfo>,
    addedToGroupCount: number,
    addedToGroupName: string,
    removedFromGroupCount: number,
    removedFromGroupName: string): void {

    let notification: IMultipleResultNotificationOptions;

    if (result.errors && result.errors.length && result.errors[0].code === CommonErrorCode.GroupNotFound) {
      // TODO: how can we determine if the "to" group or the "from" group (or both) are not found?
      notification = {
        errorSummary: this.$translate.instant('errorGroupNotFound', {
          ITEM_NAME: L10n.elideIfLong(addedToGroupName || removedFromGroupName)
        })
      };
    }
    else {

      let successId: string;
      let errorId: string;
      const messageArgs = {
        USER_NAME: L10n.elideIfLong(nameFor(users.first(), this.$translate))
      };

      // determine result notification strings and args
      if (addedToGroupCount > 0) {
        if (removedFromGroupCount > 0) {
          successId = 'groupMembershipChanged_usersAddedToGroupsAndRemovedFromGroups';
          errorId = 'groupMembershipChanged_usersAddedToGroupsAndRemovedFromGroups_failed';
          angular.extend(messageArgs,
            {
              ADD_COUNT: addedToGroupCount,
              ADD_NAME: L10n.elideIfLong(addedToGroupName),
              REMOVE_COUNT: removedFromGroupCount,
              REMOVE_NAME: L10n.elideIfLong(removedFromGroupName)
            });
        } else {
          successId = 'groupMembershipChanged_usersAddedToGroups';
          errorId = 'groupMembershipChanged_usersAddedToGroups_failed';
          angular.extend(messageArgs,
            {
              ADD_COUNT: addedToGroupCount,
              ADD_NAME: L10n.elideIfLong(addedToGroupName)
            });
        }
      }
      else {
        successId = 'groupMembershipChanged_usersRemovedFromGroups';
        errorId = 'groupMembershipChanged_usersRemovedFromGroups_failed';
        angular.extend(messageArgs,
          {
            REMOVE_COUNT: removedFromGroupCount,
            REMOVE_NAME: L10n.elideIfLong(removedFromGroupName)
          });
      }

      // TODO: this doesn't seem right - do the errors apply to users or groups? I don't think
      // we can calculate the success count if errors can apply to groups.
      notification = MultipleResultNotificationOptions.create(
        new MultipleItemResult(users, result),
        {
          describeSuccessSummary: (successCount: number) => {
            angular.extend(messageArgs, {USER_COUNT: successCount});
            return this.$translate.instant(successId, messageArgs, L10n.Interpolations.Count);
          },
          describeErrorSummary: (errorCount: number) => {
            angular.extend(messageArgs, {USER_COUNT: errorCount});
            return this.$translate.instant(errorId, messageArgs, L10n.Interpolations.Count);
          },
          describeErrorDetail: (errorItem: IErrorItem<IUserInfo>) => {
            return {
              itemName: errorItem.item.username,
              itemUrl: getUrlForUser(<IUserInfo>errorItem.item),
              errorMessage: this.errorMessageFor(errorItem)
            };
          }
        }
      );
    }

    // pass the notification object to the service for displaying
    this.contentActionNotification.notify(notification);
  }

  private errorMessageFor(errorItem: IErrorItem<IUserInfo>) {
    switch (errorItem.error.code) {
      case GroupErrorCode.InvalidGroupMembershipUpdate:
        return this.$translate.instant('errorInvalidAllUsersGroupMembershipUpdate');
      case CommonErrorCode.SiteUserNotFound:
        return this.$translate.instant('errorUserNotFound');
      case CommonErrorCode.SystemUserNotFound:
        return this.$translate.instant('errorSystemUserNotFound', { USER_NAME: L10n.elideIfLong(errorItem.item.username) });
      case CommonErrorCode.GroupNotFound:
        return this.$translate.instant('errorGroupNotFound');
      default:
        return this.$translate.instant('errorUnknown');
    }
  }

}

Module.instance().service('GroupMembershipUpdateActionNotification', GroupMembershipUpdateActionNotification);

// helpers

const nameFor = (user: IUserInfo, $translate: ng.translate.ITranslateService): string => {
  return $translate.instant('userActionNameFormattedForToast', { DISPLAY_NAME: L10n.elideIfLong(user.displayName), USER_NAME: L10n.elideIfLong(user.username) });
};

// get name of a successful item
// returns empty string if no items successful
const getSuccessfulName = (users: Set<IUserInfo>, result: IResult, $translate: ng.translate.ITranslateService): string => {
  const successfulItem = getSuccessfulItem(users, result);
  return successfulItem ? nameFor(successfulItem, $translate) : '';
};

// get name of a failing item
// returns empty string if no items failed
const getFailingName = (users: Set<IUserInfo>, result: IResult, $translate: ng.translate.ITranslateService): string => {
  const failingItem = getFailingItem(users, result);
  return failingItem ? nameFor(failingItem, $translate) : '';
};

// get a successful item, useful when there is only one item that is successful
// returns undefined if no items successful
const getSuccessfulItem = (users: Set<IUserInfo>, result: IResult): IUserInfo => {
  return users.find((item: IIdItemInfo) => {
    let matchingError: IError = undefined;
    if (result.errors) {
      matchingError = _.find(result.errors, (error: IError) => {
        return item.id === error.id;
      });
    }
    return !matchingError;
  });
};

// get a failing item, useful when there is only one item that is failing
// returns undefined if no items failed
const getFailingItem = (users: Set<IUserInfo>, result: IResult): IUserInfo => {
  return users.find((item: IIdItemInfo) => {
    let matchingError: IError = undefined;
    if (result.errors) {
      matchingError = _.find(result.errors, (error: IError) => {
        return item.id === error.id;
      });
    }
    return !!matchingError;
  });
};

// get the URL to the specified user's profile page
const getUrlForUser = (user: IUserInfo) => {
  if (user.username === 'guest' && user.domainName === 'local') {
    return null;
  } else {
    const siteUrls = siteUrlsSelector(appStore.getState());
    return siteUrls.userUrl(user.username, user.domainName);
  }
};
